import {
  EventCategorizationResponse,
  EventParam,
  EventType,
} from "../interfaces/categorizationEvents";
import { KeyByAny } from "../../documentSet/interfaces/overview";
import {
  removeCategorizationAnnotation,
  setDerivedCategorizationOptions,
  updateCategorizationAnnotation,
} from "../store/metadataSlice";
import {
  CategorizationAnnotation,
  CategorizationSetType,
  ICategorization,
} from "../../configMap/interfaces/category";
import { Annotation } from "../../annotator/interfaces/annotation";
import {
  createDerivedOptionsFromResponse,
  prepareDerivedDataForUpsert,
} from "./derivedCategorization";
import { DerivedData, OptionType } from "../interfaces/classifier";
import { notification } from "antd";
import { calculateHideDelay } from "../../common/utilities/text";
import type { AppDispatch } from "../../app/store";
import { useUpsertDerivedRecordsMutation } from "../store/metadataApi";

// The trigger returned by the RTK Query mutation hook, threaded down from
// `useCategorizationEvents` so these helpers can upsert derived records.
type UpsertDerivedRecords = ReturnType<
  typeof useUpsertDerivedRecordsMutation
>[0];

export const getCategorizationValue = (
  categorizationId: string,
  categorizationAnnotations: CategorizationAnnotation[],
  urlParam: EventParam,
  value: string,
  visibleValue?: string,
) => {
  let caValue: string = value;
  if (categorizationId === urlParam.id) {
    caValue =
      urlParam.source === "visibleValue" ? (visibleValue ?? value) : value;
  } else {
    const ca = categorizationAnnotations.find(
      (ca) => ca.index === 1 && ca.categorizationId === urlParam.id,
    );
    const tempValue =
      urlParam.source === "visibleValue"
        ? (ca?.visibleValue ?? ca?.value)
        : ca?.value;
    if (!tempValue) {
      return;
    }
    caValue = tempValue;
  }

  const { separator, index } = urlParam || {};
  if (!separator || index === null) {
    return caValue;
  }

  const splittedValue = caValue.split(separator);
  return splittedValue.length <= index! ? null : splittedValue[index!].trim();
};

export const getEntityAnnotationValue = (
  entityAnnotations: Annotation[],
  urlParam: EventParam,
) => {
  const entityAnnotation = entityAnnotations.find(
    (ea) => ea.index === 1 && ea.entity.id === urlParam.id,
  );

  if (!entityAnnotation) {
    return;
  }

  return (
    entityAnnotation.entityAnnotationNormalization?.normalizedValue ||
    entityAnnotation.values?.join(" ") ||
    ""
  );
};

export const getParamValue = (
  param: EventParam | undefined,
  value: string,
  categorizationId: string,
  currentCategorizationAnnotations: CategorizationAnnotation[],
  entityAnnotations: Annotation[],
  attribute: KeyByAny | undefined,
  visibleValue?: string,
) => {
  if (!param) {
    return null;
  }

  switch (param.type) {
    case EventType.CATEGORIZATION:
      return getCategorizationValue(
        categorizationId,
        currentCategorizationAnnotations,
        param,
        value,
        visibleValue,
      );
    case EventType.ENTITY:
      return getEntityAnnotationValue(entityAnnotations, param);
    case EventType.INPUT_ATTRIBUTE:
      return attribute;
    case EventType.FIXED:
      return param.value ?? null;
    default:
      return null;
  }
};

const updateCategorizationAnnotationFromEvent = (
  dispatch: AppDispatch,
  documentId: string,
  categorizationId: string,
  content: KeyByAny,
) => {
  const ca: CategorizationAnnotation = {
    documentId,
    categorizationId,
    index: 1,
    externalDataId: content.key,
    value: content.value,
    visibleValue: content.visibleValue,
  };
  dispatch(updateCategorizationAnnotation(ca));
};

const handleDerivedOptionsFromResponse = (
  response: KeyByAny,
  propertyList: string,
  categorizationId: string,
  isDerived: boolean,
  dispatch: AppDispatch,
) => {
  if (!propertyList || !response[propertyList]) {
    return;
  }

  if (isDerived) {
    const derivedOptions = createDerivedOptionsFromResponse(
      response[propertyList],
      categorizationId,
    );
    dispatch(
      setDerivedCategorizationOptions({
        categorizationId,
        options: derivedOptions,
      }),
    );
  } else {
    dispatch(
      setDerivedCategorizationOptions({
        categorizationId,
        options: response[propertyList],
      }),
    );
  }
};

const upsertDerivedContent = async (
  content: DerivedData,
  upsertDerivedRecords: UpsertDerivedRecords,
  organizationId: string,
  dispatch: AppDispatch,
  documentId: string,
  categorizationId: string,
): Promise<OptionType | DerivedData | undefined> => {
  try {
    const dataToUpsert = prepareDerivedDataForUpsert(content);
    const upsertRequest = {
      organizationId,
      data: [dataToUpsert],
    };
    const results = await upsertDerivedRecords(upsertRequest).unwrap();

    if (results && results.length > 0) {
      const createdOption = results[0];
      return {
        key: createdOption.key,
        value: createdOption.value,
        label: createdOption.label,
        visibleValue: createdOption.visibleValue,
      };
    }

    return content;
  } catch (error: unknown) {
    console.error("Failed to create derived record from event:", error);

    const errorMessage =
      "Something went wrong when saving categorization value (saving to external data).";
    notification.error({
      message: "Categorization Save Failed",
      description: errorMessage,
      duration: calculateHideDelay(errorMessage),
    });

    dispatch(
      removeCategorizationAnnotation({
        documentId,
        categorizationId,
        index: 1,
      }),
    );
  }
};

const handleCategorizationAnnotation = (
  content: KeyByAny | undefined,
  onLoad: boolean,
  currentCategorizationAnnotations: CategorizationAnnotation[],
  dispatch: AppDispatch,
  documentId: string,
  categorizationId: string,
) => {
  const doesCaExist = currentCategorizationAnnotations.find(
    (ca) =>
      ca.documentId === documentId &&
      ca.categorizationId === categorizationId &&
      ca.index === 1,
  );

  // Onload adds the CA only if it's empty (no overwrite and deletion)
  if (onLoad) {
    if (!doesCaExist && content) {
      updateCategorizationAnnotationFromEvent(
        dispatch,
        documentId,
        categorizationId,
        content,
      );
    }
    return;
  }

  if (!content) {
    dispatch(
      removeCategorizationAnnotation({
        documentId,
        categorizationId,
        index: 1,
      }),
    );
    return;
  }

  updateCategorizationAnnotationFromEvent(
    dispatch,
    documentId,
    categorizationId,
    content,
  );
};

const handleResponseProperty = async (
  responseProperty: EventCategorizationResponse,
  response: KeyByAny,
  dispatch: AppDispatch,
  documentId: string,
  currentCategorizationAnnotations: CategorizationAnnotation[],
  onLoad: boolean,
  categorization: ICategorization,
  upsertDerivedRecords?: UpsertDerivedRecords,
  organizationId?: string,
) => {
  const { property, propertyList, categorizationId } = responseProperty;
  let content = !property ? response : response[property];

  const isDerived = categorization?.setType === CategorizationSetType.DERIVED;

  console.log("onloadevents response", categorization?.setType, content);

  // Handle options list (for dropdowns)
  if (propertyList) {
    handleDerivedOptionsFromResponse(
      response,
      propertyList,
      categorizationId,
      isDerived,
      dispatch,
    );
  }

  // Handle content that needs upserting (external data)
  if (isDerived && content && upsertDerivedRecords && organizationId) {
    try {
      content = await upsertDerivedContent(
        content as DerivedData,
        upsertDerivedRecords,
        organizationId,
        dispatch,
        documentId,
        categorizationId,
      );
    } catch {
      return;
    }
  }

  // Handle creating/updating categorization annotation
  handleCategorizationAnnotation(
    content,
    onLoad,
    currentCategorizationAnnotations,
    dispatch,
    documentId,
    categorizationId,
  );
};

export const createCategorizationAnnotationFromResponse = async (
  responseProperties: Array<EventCategorizationResponse>,
  response: KeyByAny,
  dispatch: AppDispatch,
  documentId: string,
  currentCategorizationAnnotations: CategorizationAnnotation[],
  onLoad: boolean,
  categorization: ICategorization,
  upsertDerivedRecords?: UpsertDerivedRecords,
  organizationId?: string,
) => {
  for (const responseProperty of responseProperties) {
    await handleResponseProperty(
      responseProperty,
      response,
      dispatch,
      documentId,
      currentCategorizationAnnotations,
      onLoad,
      categorization,
      upsertDerivedRecords,
      organizationId,
    );
  }
};

export const getBodyParams = (
  params: Array<EventParam> | undefined,
  value: string,
  categorization: ICategorization,
  categorizationAnnotations: Array<CategorizationAnnotation>,
  entityAnnotations: Array<Annotation>,
  attribute: KeyByAny | undefined,
  visibleValue?: string,
): KeyByAny => {
  const bodyParameters: KeyByAny = {};
  if (!params) {
    return bodyParameters;
  }

  for (const param of params) {
    if (!param?.property) {
      continue;
    }

    const paramValue = getParamValue(
      param,
      value,
      categorization.id,
      categorizationAnnotations,
      entityAnnotations,
      attribute,
      visibleValue,
    );
    if (paramValue) {
      bodyParameters[param.property] = paramValue;
    }
  }

  return bodyParameters;
};

export const getQueryParams = (
  params: Array<EventParam> | undefined,
  value: string,
  categorization: ICategorization,
  categorizationAnnotations: Array<CategorizationAnnotation>,
  entityAnnotations: Array<Annotation>,
  attribute: KeyByAny | undefined,
  visibleValue?: string,
): string => {
  if (!params) {
    return "";
  }

  const result = params
    .map((param) => {
      const paramValue = getParamValue(
        param,
        value,
        categorization.id,
        categorizationAnnotations,
        entityAnnotations,
        attribute,
        visibleValue,
      );
      return paramValue !== null ? `${param.property}=${paramValue}` : null;
    })
    .filter((param) => param !== null)
    .join("&");
  return `?${result}`;
};
